import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaBook,
  FaBookOpen,
  FaQuran,
  FaGraduationCap,
  FaCertificate,
  FaLanguage,
  FaPrayingHands,
  FaMosque,
  FaStar,
  FaChevronRight,
  FaGift
} from 'react-icons/fa';
import './OurPrograms.css';

const OurPrograms = ({
  courses = [],
  title = "Our Courses",
  badge = "Our Programs",
  subtitle = "Explore our comprehensive list of Quran courses online to get started today",
  showMoreLink = "/courses",
  maxDisplay = 3
}) => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Map course titles to appropriate icons
  const getCourseIcon = (title) => {
    const titleLower = title.toLowerCase();
    
    if (titleLower.includes('reading') || titleLower.includes('qaida') || titleLower.includes('noorani') || titleLower.includes('madani')) {
      return <FaBookOpen />;
    }
    if (titleLower.includes('tajweed') || titleLower.includes('recitation')) {
      return <FaQuran />;
    }
    if (titleLower.includes('memorization') || titleLower.includes('hifz') || titleLower.includes('hafiz')) {
      return <FaGraduationCap />;
    }
    if (titleLower.includes('ijazah') || titleLower.includes('certificate')) {
      return <FaCertificate />;
    }
    if (titleLower.includes('arabic') || titleLower.includes('language')) {
      return <FaLanguage />;
    }
    if (titleLower.includes('qirat') || titleLower.includes('ten qirat')) {
      return <FaStar />;
    }
    if (titleLower.includes('dua') || titleLower.includes('supplication')) {
      return <FaPrayingHands />;
    }
    if (titleLower.includes('islamic studies') || titleLower.includes('tafseer') || titleLower.includes('hadith')) {
      return <FaMosque />;
    }
    // Default icon
    return <FaBook />;
  };

  const displayCourses = courses.slice(0, maxDisplay);

  return (
    <section
      ref={sectionRef}
      className={`our-programs-section ${isVisible ? 'visible' : ''}`}
      id="our-programs"
    >
      <div className="container">
        {/* Header */}
        <div className="our-programs-header">
          <div className="programs-header-icon-wrapper">
            <FaBook className="programs-header-icon" />
          </div>
          <span className="our-programs-badge">{badge}</span>
          <h2 className="our-programs-title">{title}</h2>
          <p className="our-programs-subtitle">{subtitle}</p>
        </div>

        {/* Programs Grid */}
        <div className="programs-grid">
          {displayCourses.map((course, index) => {
            const Icon = getCourseIcon(course.title);
            return (
              <div
                key={course.id}
                className={`program-card ${isVisible ? 'visible' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Link to={`/courses/${course.slug}`} className="program-card-link">
                  <div className="program-icon-wrapper">
                    <div className="program-icon-bg">
                      <div className="program-icon">
                        {Icon}
                      </div>
                    </div>
                    {course.badge && (
                      <div className="program-badge">
                        {course.badge}
                      </div>
                    )}
                  </div>
                  
                  <div className="program-content">
                    <h3 className="program-title">{course.title}</h3>
                    <p className="program-description">{course.description}</p>
                    
                    {course.duration && course.level && (
                      <div className="program-meta">
                        <span className="program-duration">
                          <FaStar className="meta-icon" />
                          {course.duration}
                        </span>
                        <span className="program-level">
                          <FaGraduationCap className="meta-icon" />
                          {course.level}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="program-footer">
                    <span className="program-link-text">
                      Learn More
                      <FaChevronRight className="link-arrow" />
                    </span>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>

        {/* Show More Button */}
        {courses.length > maxDisplay && (
          <div className="programs-show-more">
            <Link to={showMoreLink} className="programs-show-more-btn">
              <span>Show More Courses</span>
              <FaChevronRight className="btn-icon" />
            </Link>
          </div>
        )}

        {/* Free Trial CTA */}
        <div className="programs-cta">
          <div className="programs-cta-icon">
            <FaGift />
          </div>
          <h3>Start Your Learning Journey Today</h3>
          <p>Join thousands of students learning Quran online with expert tutors</p>
          <Link to="/free-trial" className="programs-cta-btn">
            <span>Book Free Trial</span>
            <FaGift className="btn-icon" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default OurPrograms;
